import React, { useState, useEffect } from 'react';
import { Printer, Search, FileText, Plus, X } from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import { formatDate } from '../../utils/dateTimeUtils';

const ReleavingLetter = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [letters, setLetters] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    staff_id: '',
    relieving_date: '',
    reason: '',
    notice_period_served: true,
    dues_cleared: true,
    assets_returned: true
  });
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchStaffList();
    fetchLetters();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = staffList.filter(staff =>
        staff.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staff.employee_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStaff(filtered);
    } else {
      setFilteredStaff(staffList);
    }
  }, [searchTerm, staffList]);

  const fetchStaffList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/staff/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaffList(response.data);
      setFilteredStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const fetchLetters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/staff/relieving-letter`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLetters(response.data);
    } catch (error) {
      console.error('Error fetching letters:', error);
    }
  };

  const handleStaffSelect = (staff) => {
    setSelectedStaff(staff);
    setFormData({
      ...formData,
      staff_id: staff.id
    });
    setShowCreateModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/staff/relieving-letter`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Relieving letter created successfully!');
      setShowCreateModal(false);
      setSelectedStaff(null);
      setFormData({
        staff_id: '',
        relieving_date: '',
        reason: '',
        notice_period_served: true,
        dues_cleared: true,
        assets_returned: true
      });
      fetchLetters();
    } catch (error) {
      console.error('Error creating letter:', error);
      alert(error.response?.data?.detail || 'Failed to create letter');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (letter) => {
    // Note: This letter is designed for letterhead paper
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;

    // Note at top (for letterhead alignment)
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('(Print on Official Letterhead)', pageWidth / 2, 15, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    // Header - Starting lower to leave space for letterhead
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('RELIEVING LETTER', pageWidth / 2, 45, { align: 'center' });

    // Letter Number
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Letter No: ${letter.id.substring(0, 8).toUpperCase()}`, margin, 60);
    doc.text(`Date: ${formatDate(letter.issue_date)}`, pageWidth - margin, 60, { align: 'right' });

    // To Section
    doc.setFontSize(12);
    let yPos = 80;
    doc.text('To,', margin, yPos);
    yPos += 7;
    doc.setFont('helvetica', 'bold');
    doc.text(letter.staff_name, margin, yPos);
    yPos += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`Employee ID: ${letter.employee_id}`, margin, yPos);
    yPos += 15;

    // Subject
    doc.setFont('helvetica', 'bold');
    doc.text('Subject: Relieving Letter', margin, yPos);
    yPos += 15;

    // Content
    doc.setFont('helvetica', 'normal');
    const greeting = 'Dear ' + letter.staff_name.split(' ')[0] + ',';
    doc.text(greeting, margin, yPos);
    yPos += 10;

    const content1 = `This is to inform you that your resignation from the position of ${letter.designation} in the ${letter.department} department has been accepted. Your last working day with us was ${formatDate(letter.relieving_date)}.`;
    const lines1 = doc.splitTextToSize(content1, pageWidth - 2 * margin);
    doc.text(lines1, margin, yPos);
    yPos += lines1.length * 7 + 10;

    const content2 = `During your tenure with us from ${formatDate(letter.joining_date)} to ${formatDate(letter.relieving_date)}, you have been a valuable member of our team. We appreciate your contributions and dedication.`;
    const lines2 = doc.splitTextToSize(content2, pageWidth - 2 * margin);
    doc.text(lines2, margin, yPos);
    yPos += lines2.length * 7 + 10;

    // Clearance Details
    doc.text('Clearance Status:', margin, yPos);
    yPos += 7;
    doc.text(`- Notice Period: ${letter.notice_period_served ? 'Served' : 'Not Served'}`, margin + 5, yPos);
    yPos += 7;
    doc.text(`- Dues Cleared: ${letter.dues_cleared ? 'Yes' : 'No'}`, margin + 5, yPos);
    yPos += 7;
    doc.text(`- Assets Returned: ${letter.assets_returned ? 'Yes' : 'No'}`, margin + 5, yPos);
    yPos += 15;

    if (letter.reason) {
      const reasonText = `Reason for leaving: ${letter.reason}`;
      const reasonLines = doc.splitTextToSize(reasonText, pageWidth - 2 * margin);
      doc.text(reasonLines, margin, yPos);
      yPos += reasonLines.length * 7 + 10;
    }

    const closing = 'We wish you all the best for your future endeavors.';
    doc.text(closing, margin, yPos);
    yPos += 20;

    // Signature
    doc.setFont('helvetica', 'bold');
    doc.text('Authorized Signatory', margin, pageHeight - 30);
    doc.setFont('helvetica', 'normal');
    doc.text(letter.issued_by_name, margin, pageHeight - 20);

    // Save
    doc.save(`Relieving_Letter_${letter.employee_id}.pdf`);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relieving Letters</h1>
          <p className="text-sm text-gray-500 mt-1">Create and manage relieving letters for staff</p>
        </div>
      </div>

      {/* Search Staff */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search staff by name or employee ID..."
            className="pl-10 pr-4 py-2 w-full border rounded-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Staff List */}
        {searchTerm && filteredStaff.length > 0 && (
          <div className="mt-4 max-h-60 overflow-y-auto border rounded-lg">
            {filteredStaff.slice(0, 5).map((staff) => (
              <div
                key={staff.id}
                onClick={() => handleStaffSelect(staff)}
                className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 flex justify-between items-center"
              >
                <div>
                  <p className="font-medium text-gray-900">{staff.full_name}</p>
                  <p className="text-sm text-gray-500">{staff.employee_id} | {staff.designation} - {staff.department}</p>
                </div>
                <Plus className="h-5 w-5 text-blue-600" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Letters List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Generated Letters</h2>
        </div>
        <div className="p-4">
          {letters.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No letters generated yet</p>
              <p className="text-sm text-gray-400 mt-1">Search for a staff member above to create a letter</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Relieving Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {letters.map((letter) => (
                    <tr key={letter.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{letter.staff_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{letter.employee_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{letter.designation}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(letter.relieving_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(letter.issue_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handlePrint(letter)}
                          className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
                        >
                          <Printer className="h-4 w-4" />
                          Print
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && selectedStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Create Relieving Letter</h2>
              <button onClick={() => { setShowCreateModal(false); setSelectedStaff(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Staff Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Staff Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Name</p>
                    <p className="font-medium">{selectedStaff.full_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Employee ID</p>
                    <p className="font-medium">{selectedStaff.employee_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Designation</p>
                    <p className="font-medium">{selectedStaff.designation}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Department</p>
                    <p className="font-medium">{selectedStaff.department}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Joining Date</p>
                    <p className="font-medium">{formatDate(selectedStaff.joining_date)}</p>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relieving Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                  value={formData.relieving_date}
                  onChange={(e) => setFormData({ ...formData, relieving_date: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Leaving (Optional)
                </label>
                <textarea
                  rows="3"
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter reason for leaving..."
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                />
              </div>

              <div className="space-y-3">
                <h3 className="font-medium text-gray-900">Clearance Status</h3>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.notice_period_served}
                    onChange={(e) => setFormData({ ...formData, notice_period_served: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Notice period served</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.dues_cleared}
                    onChange={(e) => setFormData({ ...formData, dues_cleared: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">All dues cleared</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.assets_returned}
                    onChange={(e) => setFormData({ ...formData, assets_returned: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Company assets returned</span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); setSelectedStaff(null); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Creating...' : 'Create Letter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReleavingLetter;
